/**
 * KPIRowV2 Component
 *
 * Displays KPI summary cards for the SPLH Performance Dashboard V2.
 * Shows six key metrics with comparison data per SPLH Performance Intelligence spec:
 * 1. SPLH (with FOH/BOH subtext)
 * 2. Sales
 * 3. Labor Hours (with FOH/BOH subtext)
 * 4. Labor Dollars
 * 5. Labor %
 * 6. Check Count
 *
 * Features:
 * - Responsive grid layout (6 cols desktop, 3 tablet, 2 mobile)
 * - Comparison-aware display with delta percentages
 * - Trend indicators with metric-specific colors
 * - FOH/BOH subtext for applicable metrics
 * - Skeleton loading states
 * - Backend-calculated values displayed directly
 *
 * @module pages/common/dashboards/SPLHDashboard/components/KPIRowV2
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Box, Card, CardContent, Grid, Typography, Skeleton, Tooltip } from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
} from '@mui/icons-material';
import { formatSPLH, formatCurrency, formatNumber } from '../../utils/formatUtils';

/**
 * KPI Card definitions matching backend metric names
 * Per SPLH Performance Intelligence Feature spec Section 4
 * @constant {Array<Object>}
 */
const KPI_DEFINITIONS = [
  {
    metricName: 'SPLH',
    label: 'SPLH',
    description: 'Sales per labor hour',
    format: 'splh',
    primary: true,
    hasSubtext: true,
    subtextFormat: 'splh',
    upIsGood: true,
  },
  {
    metricName: 'Sales',
    label: 'Sales',
    description: 'Total net sales',
    format: 'currency',
    upIsGood: true,
  },
  {
    metricName: 'Check Count',
    label: 'Check Count',
    description: 'Total transactions',
    format: 'number',
    upIsGood: true,
  },
  {
    metricName: 'Labor Hours',
    label: 'Labor Hours',
    description: 'Total labor hours',
    format: 'hours',
    hasSubtext: true,
    subtextFormat: 'hours',
    neutralTrend: true,
  },
  {
    metricName: 'Labor Dollars',
    label: 'Labor Dollars',
    description: 'Total labor cost',
    format: 'currency',
    upIsGood: false, // Higher labor cost is bad
  },
  {
    metricName: 'Labor %',
    label: 'Labor %',
    description: 'Labor cost as % of sales',
    format: 'percentage',
    upIsGood: false, // Higher labor % is bad
  },
];

/**
 * Gets the trend icon component based on direction
 * @param {string} direction - Trend direction: 'UP', 'DOWN', or 'FLAT'
 * @returns {React.Component} Icon component
 */
const getTrendIcon = (direction) => {
  switch (direction?.toUpperCase()) {
    case 'UP':
      return TrendingUpIcon;
    case 'DOWN':
      return TrendingDownIcon;
    default:
      return TrendingFlatIcon;
  }
};

/**
 * Gets the color for trend indicator based on metric configuration
 * @param {string} direction - Trend direction
 * @param {Object} definition - Metric definition with upIsGood/neutralTrend flags
 * @param {Object} colors - Color configuration from config
 * @returns {string} Color hex code
 */
const getTrendColor = (direction, definition, colors) => {
  // Neutral trend metrics (like Labor Hours) always show gray
  if (definition.neutralTrend) {
    return colors.neutral;
  }

  const directionUpper = direction?.toUpperCase();

  if (directionUpper === 'UP') {
    return definition.upIsGood ? colors.positive : colors.negative;
  } else if (directionUpper === 'DOWN') {
    return definition.upIsGood ? colors.negative : colors.positive;
  }

  return colors.neutral;
};

/**
 * Formats a value based on the specified format type
 * @param {number} value - The value to format
 * @param {string} format - Format type: 'splh', 'currency', 'hours', 'percentage', 'number'
 * @returns {string} Formatted value
 */
const formatValue = (value, format) => {
  if (value === null || value === undefined) {
    return '-';
  }

  switch (format) {
    case 'splh':
      return formatSPLH(value);
    case 'currency':
      return formatCurrency(value, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    case 'hours':
      return formatNumber(Math.round(value), { decimalPlaces: 0 });
    case 'percentage':
      return `${value.toFixed(1)}%`;
    case 'number':
      return formatNumber(Math.round(value), { decimalPlaces: 0 });
    default:
      return String(value);
  }
};

/**
 * Formats delta percentage for display
 * @param {number} deltaPct - Delta percentage
 * @returns {string} Formatted delta (e.g., "+5.2%" or "-3.1%")
 */
const formatDelta = (deltaPct) => {
  if (deltaPct === null || deltaPct === undefined) {
    return '';
  }
  const sign = deltaPct >= 0 ? '+' : '';
  return `${sign}${deltaPct.toFixed(1)}%`;
};

/**
 * Formats subtext value for FOH/BOH display
 * @param {number} fohValue - FOH value
 * @param {number} bohValue - BOH value
 * @param {string} format - Format type
 * @returns {string} Formatted subtext (e.g., "FOH: $45.20 | BOH: $38.50")
 */
const formatSubtext = (fohValue, bohValue, format) => {
  if (fohValue === null && bohValue === null) {
    return null;
  }

  const fohFormatted = fohValue !== null ? formatValue(fohValue, format) : '-';
  const bohFormatted = bohValue !== null ? formatValue(bohValue, format) : '-';

  return `FOH: ${fohFormatted} | BOH: ${bohFormatted}`;
};

/**
 * Single KPI Card Component for V2 (comparison-aware with subtext support)
 * @param {Object} props - Component props
 */
const KPICardV2 = ({ definition, kpiData, loading, colors }) => {
  const { label, description, format, primary, hasSubtext, subtextFormat, neutralTrend } = definition;

  // Extract MetricValue structure
  const value = kpiData?.value;
  const currentValue = value?.current;
  const deltaPct = value?.deltaPct;
  const direction = value?.direction;

  // Extract subMetrics for FOH/BOH display
  const subMetrics = kpiData?.subMetrics;
  const fohValue = subMetrics?.foh;
  const bohValue = subMetrics?.boh;
  const fohComparison = subMetrics?.fohComparison;
  const bohComparison = subMetrics?.bohComparison;

  // Format values
  const displayValue = !loading ? formatValue(currentValue, format) : '-';
  const deltaDisplay = !loading && deltaPct !== null ? formatDelta(deltaPct) : '';

  // Format subtext
  const subtextDisplay = hasSubtext && !loading ? formatSubtext(fohValue, bohValue, subtextFormat || format) : null;

  // Build tooltip for subtext comparison
  const subtextTooltip = hasSubtext && subMetrics && !loading
    ? `FOH: ${formatValue(fohValue, subtextFormat || format)} (was ${formatValue(fohComparison, subtextFormat || format)}) | BOH: ${formatValue(bohValue, subtextFormat || format)} (was ${formatValue(bohComparison, subtextFormat || format)})`
    : '';

  const TrendIcon = getTrendIcon(direction);
  const trendColor = getTrendColor(direction, definition, colors);
  const showTrend = !loading && direction && direction !== 'FLAT' && !neutralTrend;

  return (
    <Card
      sx={{
        height: '100%',
        borderRadius: 2,
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: primary ? `2px solid ${colors.primary || '#7C3AED'}` : 'none',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        },
        transition: 'box-shadow 0.2s ease-in-out',
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        {/* Label */}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontWeight: 500, mb: 0.5 }}
        >
          {label}
        </Typography>

        {/* Value row */}
        {loading ? (
          <Skeleton variant="text" width={80} height={36} />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
              }}
            >
              {displayValue}
            </Typography>
            {showTrend && (
              <TrendIcon
                sx={{
                  color: trendColor,
                  fontSize: 20,
                }}
              />
            )}
            {neutralTrend && direction && direction !== 'FLAT' && (
              <TrendIcon
                sx={{
                  color: colors.neutral,
                  fontSize: 20,
                }}
              />
            )}
          </Box>
        )}

        {/* Delta row */}
        {loading ? (
          <Skeleton variant="text" width={60} height={18} sx={{ mt: 0.5 }} />
        ) : (
          deltaDisplay && (
            <Typography
              variant="body2"
              sx={{
                color: trendColor,
                fontWeight: 500,
                fontSize: '0.8125rem',
                mt: 0.25,
              }}
            >
              {deltaDisplay} vs comparison
            </Typography>
          )
        )}

        {/* Subtext row (FOH/BOH) */}
        {loading && hasSubtext ? (
          <Skeleton variant="text" width="90%" height={14} sx={{ mt: 0.5 }} />
        ) : (
          subtextDisplay && (
            <Tooltip title={subtextTooltip} arrow placement="top">
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  mt: 0.5,
                  display: 'block',
                  fontSize: '0.7rem',
                  cursor: 'help',
                }}
              >
                {subtextDisplay}
              </Typography>
            </Tooltip>
          )
        )}

        {/* Description (only show if no subtext) */}
        {!hasSubtext && (
          loading ? (
            <Skeleton variant="text" width="80%" height={14} sx={{ mt: 0.5 }} />
          ) : (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.5, display: 'block', fontSize: '0.7rem' }}
            >
              {description}
            </Typography>
          )
        )}
      </CardContent>
    </Card>
  );
};

KPICardV2.propTypes = {
  definition: PropTypes.shape({
    metricName: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    format: PropTypes.oneOf(['splh', 'currency', 'hours', 'percentage', 'number']).isRequired,
    primary: PropTypes.bool,
    hasSubtext: PropTypes.bool,
    subtextFormat: PropTypes.string,
    upIsGood: PropTypes.bool,
    neutralTrend: PropTypes.bool,
  }).isRequired,
  kpiData: PropTypes.shape({
    metricName: PropTypes.string,
    value: PropTypes.shape({
      current: PropTypes.number,
      comparison: PropTypes.number,
      delta: PropTypes.number,
      deltaPct: PropTypes.number,
      direction: PropTypes.string,
    }),
    subMetrics: PropTypes.shape({
      foh: PropTypes.number,
      boh: PropTypes.number,
      fohComparison: PropTypes.number,
      bohComparison: PropTypes.number,
    }),
  }),
  loading: PropTypes.bool,
  colors: PropTypes.shape({
    positive: PropTypes.string,
    negative: PropTypes.string,
    neutral: PropTypes.string,
    primary: PropTypes.string,
  }).isRequired,
};

/**
 * KPIRowV2 Component
 *
 * Displays a responsive grid of 6 KPI cards with comparison data.
 * Metrics match SPLH Performance Intelligence spec:
 * SPLH, Sales, Labor Hours, Labor Dollars, Labor %, Check Count
 *
 * @param {Object} props - Component props
 * @param {Array} props.kpis - KPI data array from backend V2 API
 * @param {boolean} [props.loading=false] - Whether data is loading
 * @param {Object} props.colors - Color configuration from config
 * @returns {React.Component} KPIRowV2 component
 */
const KPIRowV2 = ({ kpis = [], loading = false, colors }) => {
  // Default colors if not provided
  const chartColors = colors || {
    positive: '#10B981',
    negative: '#EF4444',
    neutral: '#6B7280',
    primary: '#7C3AED',
  };

  // Create a map of KPI data by metricName for quick lookup
  const kpiMap = new Map();
  if (kpis && Array.isArray(kpis)) {
    kpis.forEach((kpi) => {
      kpiMap.set(kpi.metricName, kpi);
    });
  }

  return (
    <Box>
      <Grid container spacing={2}>
        {KPI_DEFINITIONS.map((definition) => (
          <Grid
            item
            xs={6}
            sm={4}
            md={2}
            key={definition.metricName}
          >
            <KPICardV2
              definition={definition}
              kpiData={kpiMap.get(definition.metricName)}
              loading={loading}
              colors={chartColors}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

KPIRowV2.propTypes = {
  /** KPI data array from backend V2 API */
  kpis: PropTypes.arrayOf(
    PropTypes.shape({
      metricName: PropTypes.string.isRequired,
      value: PropTypes.shape({
        current: PropTypes.number,
        comparison: PropTypes.number,
        delta: PropTypes.number,
        deltaPct: PropTypes.number,
        direction: PropTypes.string,
      }),
      subMetrics: PropTypes.shape({
        foh: PropTypes.number,
        boh: PropTypes.number,
        fohComparison: PropTypes.number,
        bohComparison: PropTypes.number,
      }),
    })
  ),
  /** Whether data is loading */
  loading: PropTypes.bool,
  /** Color configuration */
  colors: PropTypes.shape({
    positive: PropTypes.string,
    negative: PropTypes.string,
    neutral: PropTypes.string,
    primary: PropTypes.string,
  }),
};

export default KPIRowV2;
